import CDS from "design-system";
import { roleStyles } from "components/Tag/UserTag/helpers";
import { EmailRecipient } from "features/Resources/Email/models/EmailRecipient";
import { useEmailRecipientDetails } from "features/Resources/Email/hooks";
import { appendEmailAbbreviation } from "features/Resources/Email/helpers";
import type { UserRole } from "features/User/models";

const ROLE_LABEL: Record<string, string> = {
  new_hire: "New hire",
  manager: "Manager",
  buddy: "Buddy",
  people_team_contact: "People Team",
  recruiter: "Recruiter",
  it_team_contact: "IT",
};

interface EmailRecipientTagProps {
  recipient: EmailRecipient;
  remove?: () => void;
  disabled?: boolean;
}

/**
 * Maps an EmailRecipient (user / role / custom email) to the shared
 * CDS.RecipientChip. The chip label includes the email-type suffix
 * ("Name (w)" or "(p)") so it's obvious which address the email will go to;
 * initials are computed from the name only so the avatar isn't polluted.
 *
 * Detection is value-based: the helpers' `isCustomEmailRecipient` etc. check
 * whether the property *key* exists, but the BE serializes all fields
 * (including null `customEmail`), so we check the actual resolved value here.
 */
export default function EmailRecipientTag({
  recipient,
  remove = () => {},
  disabled = false,
}: EmailRecipientTagProps) {
  const { displayName, src, role } = useEmailRecipientDetails(recipient);
  const onRemove = disabled ? undefined : remove;

  const customEmail = (recipient as any)?.customEmail as string | null | undefined;
  const relationId = (recipient as any)?.onboardingJourneyNetworkRelationId as string | null | undefined;
  const emailType = (recipient as any)?.emailType as string | undefined;

  if (customEmail) {
    return (
      <CDS.RecipientChip
        variant="email"
        label={customEmail}
        size="sm"
        bordered
        remove={onRemove}
      />
    );
  }

  if (relationId && role) {
    const styles = roleStyles(role as UserRole);
    const baseLabel = ROLE_LABEL[role] ?? role;
    return (
      <CDS.RecipientChip
        variant="role"
        label={appendEmailAbbreviation(baseLabel, emailType ?? "")}
        roleColors={{ bg: styles.light, fg: styles.dark }}
        size="sm"
        bordered
        remove={onRemove}
      />
    );
  }

  const baseName = displayName || "Loading";
  const initials = baseName.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("");
  return (
    <CDS.RecipientChip
      variant="user"
      label={appendEmailAbbreviation(baseName, emailType ?? "")}
      initials={initials}
      avatarUrl={src}
      size="sm"
      bordered
      remove={onRemove}
    />
  );
}
